import { Database, get, ref, remove, set, update } from 'firebase/database';
import { Registration } from '@opanuj/shared/types';

/** @deprecated use reservationDelete instead */
// deletes reservations for a given slot. Optionally can delete only reservations for particular students.
export const reservationsDelete = async (database: Database, { slotId, studentNames }: { slotId: string, studentNames?: string[] }): Promise<void> => {
	const registrations: { [id: string]: Registration } = (
		await get(ref(database, `registrations`))
	).val();
	for (const [registrationId, registration] of Object.entries(
		registrations
	)) {
		let newLessons = [];
		let found = false;
		for (const [, lesson] of Object.entries(registration.lessons) as [
			string,
			any
		]) {
			if (
				lesson.slot === slotId &&
				(!studentNames || (studentNames && studentNames.includes(lesson.student.name)))
			) {
				found = true;
			} else {
				newLessons.push(lesson);
			}
			if (found) {
				if (newLessons.length) {
					return set(
						ref(
							database,
							`registrations/${registrationId}/lessons`
						),
						newLessons
					);
				} else {
					return await set(
						ref(database, `registrations/${registrationId}`),
						null
					);
				}
			}
		}
	}
	return;
};

export const reservationDelete = async (
	database: Database,
	{ reservationId, reservationUid, slotIds = [] }: { reservationId?: string; reservationUid?: string; slotIds?: string[] },
): Promise<void> => {
	if (!reservationId && !reservationUid && !slotIds.length) {
		return;
	}

	const targetSlotIds = Array.from(new Set(slotIds.filter(Boolean)));
	const slotsSnapshot = await get(ref(database, 'slots'));
	const slots = slotsSnapshot.val() ?? {};
	const updates: Record<string, any> = {};

	for (const [slotId, slotValue] of Object.entries(slots as Record<string, any>)) {
		const slot = slotValue;
		if (!slot || !Array.isArray(slot.reservations)) {
			continue;
		}

		const shouldRemoveSlot = targetSlotIds.length === 0 || targetSlotIds.includes(slotId);
		if (!shouldRemoveSlot) {
			continue;
		}

		slot.reservations = slot.reservations.filter((reservation: any) => {
			const matchesReservationId = reservationId ? (reservation?.reservationId === reservationId || reservation?.id === reservationId) : false;
			const matchesReservationUid = reservationUid ? reservation?.uid === reservationUid : false;
			return !(matchesReservationId || matchesReservationUid);
		});

		updates[`slots/${slotId}/reservations`] = slot.reservations;
	}

	const bookingEntries = (await get(ref(database, 'bookings'))).val() ?? {};
	const bookingKey = Object.keys(bookingEntries).find((key) => {
		const booking = bookingEntries[key];
		const matchesReservationId = reservationId ? (booking?.id === reservationId || key === reservationId) : false;
		const matchesReservationUid = reservationUid ? (booking?.uid === reservationUid || key === reservationUid) : false;
		return matchesReservationId || matchesReservationUid;
	});

	if (bookingKey) {
		if (targetSlotIds.length === 0) {
			if (Object.keys(updates).length) {
				await update(ref(database), updates);
			}
			await remove(ref(database, `bookings/${bookingKey}`));
			return;
		}

		const booking = bookingEntries[bookingKey];
		const remainingLessons = (booking?.lessons ?? []).filter((lesson: any) => !targetSlotIds.includes(lesson?.slot));
		if (remainingLessons.length) {
			updates[`bookings/${bookingKey}/lessons`] = remainingLessons;
		} else {
			updates[`bookings/${bookingKey}`] = null;
		}
	}

	if (Object.keys(updates).length) {
		await update(ref(database), updates);
	}

	if (bookingKey && targetSlotIds.length > 0) {
		const booking = bookingEntries[bookingKey];
		const remainingLessons = (booking?.lessons ?? []).filter((lesson: any) => !targetSlotIds.includes(lesson?.slot));
		if (!remainingLessons.length) {
			await remove(ref(database, `bookings/${bookingKey}`));
		}
	}
};
